import { fmt, fmtHrs, COST_TYPE_META } from './useRecipeState'

const LivePreviewRail = ({
  preview, previewCalc,
  vars, pvSel, setPvSel,
  inputSlots, slotValues, setSlotValues,
  classBreakdowns, setClassBreakdowns,
  minimumTotal
}) => {
  const { effects } = preview
  const calc = previewCalc

  const hasSizeClasses = (inputSlots || []).some(s => s.isSizeClassInput && (s.quantityClasses || []).length > 0)

  return (
    <div>
      {/* Project Inputs */}
      {inputSlots && inputSlots.length > 0 && (
        <div className="rail-section">
          <div className="rail-section-header">Project inputs</div>
          <div className="pi-group">
            {inputSlots.map((slot, i) => (
              <label key={i} className="pi-row">
                <span className="pi-label">{slot.label || slot.key}</span>
                <input
                  type="number"
                  className="pi-input"
                  value={slotValues[slot.key] ?? slot.defaultValue ?? ''}
                  onChange={e => setSlotValues({ ...slotValues, [slot.key]: e.target.value ? parseFloat(e.target.value) : 0 })}
                />
                <span className="pi-unit">{slot.unit}</span>
              </label>
            ))}
          </div>

          {/* Size class breakdowns */}
          {hasSizeClasses && inputSlots.filter(s => s.isSizeClassInput).map(slot => (
            <div key={slot.key} style={{ marginTop: 12, padding: 10, background: 'var(--bg-2)', borderRadius: 'var(--r)', border: '1px solid var(--line)' }}>
              <div className="overline-label" style={{ marginBottom: 6 }}>
                {slot.label} Size Classes
              </div>
              {(slot.quantityClasses || []).map(cls => (
                <div key={cls.classKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ flex: 1, fontSize: 11, color: 'var(--ink)' }}>{cls.label || cls.classKey}</span>
                  <input
                    type="number"
                    className="pi-input"
                    value={(classBreakdowns[slot.key] || {})[cls.classKey] ?? ''}
                    onChange={e => {
                      const bk = { ...(classBreakdowns[slot.key] || {}), [cls.classKey]: e.target.value ? parseFloat(e.target.value) : 0 }
                      setClassBreakdowns({ ...classBreakdowns, [slot.key]: bk })
                    }}
                    style={{ width: 60 }}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Site Configuration (Variation selectors) */}
      {vars && vars.length > 0 && (
        <div className="rail-section">
          <div className="rail-section-header">Site configuration</div>
          <div className="pi-group" style={{ padding: '10px 14px' }}>
            {vars.map((v, i) => (
              <div key={i}>
                <div className="pi-label" style={{ marginBottom: 4 }}>
                  {v.label || v.attributeId}
                </div>
                {v.type === 'select' && (
                  <select
                    className="rail-select"
                    value={pvSel[v.attributeId] ?? v.defaultValue ?? ''}
                    onChange={e => setPvSel({ ...pvSel, [v.attributeId]: e.target.value })}
                  >
                    {(v.options || []).map(o => (
                      <option key={o.value} value={o.value}>{o.label || o.value}</option>
                    ))}
                  </select>
                )}
                {v.type === 'boolean' && (
                  <label className="rail-toggle">
                    <input
                      type="checkbox"
                      checked={pvSel[v.attributeId] === true}
                      onChange={e => setPvSel({ ...pvSel, [v.attributeId]: e.target.checked })}
                    />
                    <span className="toggle-track" />
                    <span style={{ fontSize: 12, color: pvSel[v.attributeId] ? 'var(--primary-2)' : 'var(--ink-4)' }}>
                      {pvSel[v.attributeId] ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                )}
                {v.type === 'range' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="range"
                      min={v.min || 0} max={v.max || 100} step={v.step || 1}
                      value={pvSel[v.attributeId] ?? v.defaultValue ?? v.min ?? 0}
                      onChange={e => setPvSel({ ...pvSel, [v.attributeId]: parseFloat(e.target.value) })}
                      style={{ flex: 1, accentColor: 'var(--primary)' }}
                    />
                    <span className="mono-num">{pvSel[v.attributeId] ?? v.defaultValue ?? v.min ?? 0}{v.unit ? ` ${v.unit}` : ''}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Computed Recipe — grid with mono numbers right-aligned */}
      <div className="rail-section">
        <div className="rail-section-header">
          Computed recipe
          {calc.rows.length > 0 && (
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-4)' }}>
              {calc.rows.length} line{calc.rows.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div>
          {calc.rows.map((row, i) => {
            const costMeta = COST_TYPE_META[row.costType] || COST_TYPE_META.labor
            const isExcluded = row._excluded
            const isAdded = row._added
            const isAggregate = row.quantityMode === 'aggregate'

            return (
              <div key={i} className={`cr${isExcluded ? ' cr-excluded' : ''}${isAdded ? ' cr-added' : ''}`}>
                <span className={`chip chip-sm ${costMeta.chipClass}`}>
                  {costMeta.label.slice(0, 3)}
                </span>
                <span className="cr-name">
                  {isAggregate && <span style={{ fontStyle: 'italic', marginRight: 3 }}>{'\u03A3'}</span>}
                  {row.description || 'Untitled'}
                </span>
                {!isExcluded && (
                  <>
                    <span className="cr-hrs">{row.hours > 0 ? fmtHrs(row.hours) : ''}</span>
                    <span className="cr-cost">{fmt(row.cost)}</span>
                  </>
                )}
                {isExcluded && (
                  <>
                    <span className="cr-hrs" />
                    <span className="cr-cost" style={{ color: 'var(--ink-4)' }}>&mdash;</span>
                  </>
                )}
                <span className={`cr-mod${isAdded ? ' plus' : ''}${isExcluded ? ' minus' : ''}`}>
                  {isAdded ? '+' : isExcluded ? '\u2212' : ''}
                </span>
              </div>
            )
          })}
        </div>

        {/* Variation effect annotations */}
        {effects.length > 0 && (
          <div style={{ paddingTop: 6 }}>
            {effects.map((eff, i) => (
              <div key={i} style={{ fontSize: 10, color: 'var(--ink-4)', display: 'flex', gap: 4, alignItems: 'center', padding: '2px 0' }}>
                <span style={{
                  width: 5, height: 5, borderRadius: '50%',
                  background: eff.type === 'exclude' ? 'var(--disposal)' : eff.type === 'add' ? 'var(--labor)' : eff.type === 'include' ? 'var(--primary)' : 'var(--accent)'
                }} />
                <span style={{ fontStyle: 'italic' }}>{eff.variation}: {eff.type} {eff.target}</span>
              </div>
            ))}
          </div>
        )}

        {/* Rollup row — three categories in grid */}
        <div className="rollup">
          <div className="rollup-cell">
            <span className="overline-label">Labor</span>
            <span className="mono-num">{fmt(calc.totalLaborCost)}</span>
          </div>
          <div className="rollup-cell">
            <span className="overline-label">Equipment</span>
            <span className="mono-num">{fmt(calc.totalEquipCost)}</span>
          </div>
          <div className="rollup-cell">
            <span className="overline-label">Material</span>
            <span className="mono-num">{fmt(calc.totalMaterialCost || 0)}</span>
          </div>
        </div>
      </div>

      {/* Material Lines */}
      {calc.materialLines && calc.materialLines.length > 0 && (
        <div className="rail-section">
          <div className="rail-section-header">Materials</div>
          {calc.materialLines.map((ml, i) => (
            <div key={i} className="cr" style={{ gridTemplateColumns: '1fr auto' }}>
              <span className="cr-name">{ml.label || 'Untitled'}</span>
              <span className="cr-cost">{ml.computedQty.toFixed(1)} {ml.unit}</span>
            </div>
          ))}
        </div>
      )}

      {/* Headline card — collapsed Computed Cost + Estimator Will Use */}
      <div className="hl-card">
        <span className="overline-label">Estimator will use</span>
        <div className="hl-row">
          <span className="hl-num">{fmt(calc.effectiveTotal)}</span>
          {minimumTotal > 0 && calc.grandTotal < minimumTotal && (
            <span className="hl-cap">(min applied)</span>
          )}
        </div>
        <div className="hl-detail">
          <span>Computed</span>
          <span className="hl-detail-val">{fmt(calc.grandTotal)}</span>
        </div>
        {minimumTotal > 0 && (
          <div className="hl-detail" style={{ marginTop: 4, paddingTop: 0, borderTop: 'none' }}>
            <span>Recipe minimum</span>
            <span className="hl-detail-val">{fmt(minimumTotal)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export default LivePreviewRail
